import React from 'react';
import Dropdown from '../../../../components/Dropdown';
import { DropdownItem } from '../../../../components/Dropdown/config';
import SearchableProjectDropdown from '../../../../components/SearchableProjectDropdown';
import SearchableClientDropdown from '../../../../components/SearchableClientDropdown';
import SearchableContactDropdown from '../../../../components/SearchableContactDropdown';
import {
  NcrFormData,
  NcrFormErrors,
  NCR_TYPE_OPTIONS,
  NCR_STATUS_OPTIONS,
  NCR_SEVERITY_OPTIONS,
  NCR_CLASSIFICATION_OPTIONS,
} from '../../config';

interface TeamMember {
  id: number | string;
  first_name: string;
  last_name: string;
}

interface NcrFormFieldsProps {
  formData: NcrFormData;
  onChange: <K extends keyof NcrFormData>(field: K, value: NcrFormData[K]) => void;
  /** Selected project/stage from SearchableProjectDropdown -> project_id + stage_id */
  onProjectChange: (projectId: string | number | null, project?: any) => void;
  /** Selected issuing client (external) -> client_id (and clears contact_id) */
  onClientChange: (clientId: string | number | null) => void;
  /** Selected issuing contact (external) -> contact_id */
  onContactChange: (contactId: string | number | null) => void;
  teamMembers: TeamMember[];
  errors?: NcrFormErrors;
  /** Show "*" markers on the create-required fields (Add modal only). */
  required?: boolean;
}

const NcrFormFields: React.FC<NcrFormFieldsProps> = ({
  formData,
  onChange,
  onProjectChange,
  onClientChange,
  onContactChange,
  teamMembers,
  errors = {},
  required = false,
}) => {
  const isExternal = formData.type === 'External';
  const req = required ? ' *' : '';

  const teamMemberItems: DropdownItem[] = teamMembers.map(tm => ({
    id: tm.id,
    label: `${tm.first_name} ${tm.last_name}`,
    value: tm.id.toString(),
  }));
  const statusItems: DropdownItem[] = NCR_STATUS_OPTIONS.map(s => ({ id: s, label: s, value: s }));
  const severityItems: DropdownItem[] = NCR_SEVERITY_OPTIONS.map(s => ({ id: s, label: s, value: s }));
  const classificationItems: DropdownItem[] = NCR_CLASSIFICATION_OPTIONS.map(s => ({ id: s, label: s, value: s }));

  return (
    <>
      {/* ============ NCR GENERAL ============ */}
      <div className="form-section">
        <h3 className="section-title">NCR General</h3>

        <div className="form-group">
          <label>Type *</label>
          <div className="ncr-type-selector">
            {NCR_TYPE_OPTIONS.map(option => (
              <label key={option} className="radio-option">
                <input
                  type="radio"
                  name="ncrType"
                  value={option}
                  checked={formData.type === option}
                  onChange={() => onChange('type', option)}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="ncr-title">Title{req}</label>
          <input
            id="ncr-title"
            type="text"
            value={formData.title}
            onChange={(e) => onChange('title', e.target.value)}
            placeholder="Short summary of the non-conformance"
            className={errors.title ? 'error' : ''}
          />
          {errors.title && <div className="error-message">{errors.title}</div>}
        </div>

        <div className="form-row">
          <div className="form-group" style={{ flex: 2 }}>
            <label>Project / Stage</label>
            <SearchableProjectDropdown
              value={formData.project_id ? parseInt(formData.project_id, 10) : null}
              onChange={onProjectChange}
              placeholder="Search and select project or stage..."
              allowNone={true}
            />
          </div>
          <div className="form-group">
            <label>Raised By{req}</label>
            <Dropdown
              items={teamMemberItems}
              value={formData.raised_by}
              onChange={(value) => onChange('raised_by', value)}
              placeholder="Select team member"
              searchable
              error={!!errors.raised_by}
            />
            {errors.raised_by && <div className="error-message">{errors.raised_by}</div>}
          </div>
          <div className="form-group">
            <label htmlFor="ncr-date-raised">Date Raised *</label>
            <input
              id="ncr-date-raised"
              type="date"
              value={formData.date_raised}
              onChange={(e) => onChange('date_raised', e.target.value)}
              className={errors.date_raised ? 'error' : ''}
            />
            {errors.date_raised && <div className="error-message">{errors.date_raised}</div>}
          </div>
        </div>

        {isExternal && (
          <div className="form-row">
            <div className="form-group">
              <label>Issuing Party (Client){req}</label>
              <SearchableClientDropdown
                value={formData.client_id || null}
                onChange={onClientChange}
                placeholder="Select issuing client..."
                allowNone={true}
                noneLabel="No client"
                className={errors.client_id ? 'error' : ''}
              />
              {errors.client_id && <div className="error-message">{errors.client_id}</div>}
            </div>
            <div className="form-group">
              <label>Contact{req}</label>
              <SearchableContactDropdown
                value={formData.contact_id || null}
                onChange={onContactChange}
                clientId={formData.client_id ? parseInt(formData.client_id, 10) : null}
                placeholder="Select contact..."
                allowNone={true}
                className={errors.contact_id ? 'error' : ''}
              />
              {errors.contact_id && <div className="error-message">{errors.contact_id}</div>}
            </div>
          </div>
        )}
      </div>

      {/* ============ CLASSIFICATION ============ */}
      <div className="form-section">
        <h3 className="section-title">Classification</h3>
        <div className="form-row">
          <div className="form-group" style={{ flex: 2 }}>
            <label>NCR Type{req}</label>
            <Dropdown
              items={classificationItems}
              value={formData.classification}
              onChange={(value) => onChange('classification', value)}
              placeholder="Select nonconformance type"
              error={!!errors.classification}
            />
            {errors.classification && <div className="error-message">{errors.classification}</div>}
          </div>
          <div className="form-group">
            <label>Severity{req}</label>
            <Dropdown
              items={severityItems}
              value={formData.severity}
              onChange={(value) => onChange('severity', value)}
              placeholder="Select severity"
              error={!!errors.severity}
            />
            {errors.severity && <div className="error-message">{errors.severity}</div>}
          </div>
        </div>
      </div>

      {/* ============ DETAILS (internal vs external) ============ */}
      <div className="form-section">
        <h3 className="section-title">NCR Details</h3>

        {/* Description (internal) ↔ Reported Externally (external) */}
        {isExternal ? (
          <div className="form-group">
            <label htmlFor="ncr-external-details">NCR Details Reported Externally{req}</label>
            <textarea
              id="ncr-external-details"
              value={formData.external_reported_details}
              onChange={(e) => onChange('external_reported_details', e.target.value)}
              placeholder="Summary of the nonconformance as raised by the external party..."
              rows={3}
              className={errors.external_reported_details ? 'error' : ''}
            />
            {errors.external_reported_details && <div className="error-message">{errors.external_reported_details}</div>}
          </div>
        ) : (
          <div className="form-group">
            <label htmlFor="ncr-description">Description of Nonconformance{req}</label>
            <textarea
              id="ncr-description"
              value={formData.description}
              onChange={(e) => onChange('description', e.target.value)}
              placeholder="What happened, when, where, and who was involved..."
              rows={3}
              className={errors.description ? 'error' : ''}
            />
            {errors.description && <div className="error-message">{errors.description}</div>}
          </div>
        )}

        {/* Requirement Not Met + Impact — captured for both types */}
        <div className="form-group">
          <label htmlFor="ncr-requirement">Requirement Not Met{req}</label>
          <textarea
            id="ncr-requirement"
            value={formData.requirement_not_met}
            onChange={(e) => onChange('requirement_not_met', e.target.value)}
            placeholder="e.g. QMS procedure, client brief, standard, drawing requirement..."
            rows={2}
            className={errors.requirement_not_met ? 'error' : ''}
          />
          {errors.requirement_not_met && <div className="error-message">{errors.requirement_not_met}</div>}
        </div>
        <div className="form-group">
          <label htmlFor="ncr-impact">Impact or Potential Consequence{req}</label>
          <textarea
            id="ncr-impact"
            value={formData.impact}
            onChange={(e) => onChange('impact', e.target.value)}
            placeholder="What was the immediate impact or potential consequence?"
            rows={2}
            className={errors.impact ? 'error' : ''}
          />
          {errors.impact && <div className="error-message">{errors.impact}</div>}
        </div>

        {/* Internal Investigation — external only */}
        {isExternal && (
          <div className="form-group">
            <label htmlFor="ncr-internal-investigation">Internal Investigation{req}</label>
            <textarea
              id="ncr-internal-investigation"
              value={formData.internal_investigation}
              onChange={(e) => onChange('internal_investigation', e.target.value)}
              placeholder="Internal assessment — do we accept the NCR? Rationale, root cause..."
              rows={3}
              className={errors.internal_investigation ? 'error' : ''}
            />
            {errors.internal_investigation && <div className="error-message">{errors.internal_investigation}</div>}
          </div>
        )}
      </div>

      {/* ============ ACTIONS ============ */}
      <div className="form-section">
        <h3 className="section-title">Actions</h3>
        <div className="form-group">
          <label htmlFor="ncr-action">Action To Be Taken</label>
          <textarea
            id="ncr-action"
            value={formData.action_to_be_taken}
            onChange={(e) => onChange('action_to_be_taken', e.target.value)}
            placeholder="Corrective action — what will be done to prevent recurrence?"
            rows={2}
          />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="ncr-status">Status</label>
            <Dropdown
              items={statusItems}
              value={formData.status}
              onChange={(value) => onChange('status', value)}
              placeholder="Select status"
            />
          </div>
          <div className="form-group">
            <label htmlFor="ncr-target-date">Target Close-Out Date</label>
            <input
              id="ncr-target-date"
              type="date"
              value={formData.target_close_out_date}
              onChange={(e) => onChange('target_close_out_date', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="ncr-actual-date">Actual Close-Out Date</label>
            <input
              id="ncr-actual-date"
              type="date"
              value={formData.actual_close_out_date}
              onChange={(e) => onChange('actual_close_out_date', e.target.value)}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default NcrFormFields;
